import { animate, state, style, transition, trigger } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { SearchFormParameterGroup, UserPreference } from 'src/app/core/models/user-preference';
import { UserService } from 'src/app/core/services/user-service.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { finalize } from 'rxjs/operators';
@Component({
  selector: 'laas-search-form-parameter-group',
  templateUrl: './search-form-parameter-group.component.html',
  styleUrls: ['./search-form-parameter-group.component.scss'],
  providers: [ ConfirmationService ],
  animations: [
    trigger('openClose', [
      state('open', style({
        opacity: 1
      })),
      state('closed', style({
        opacity: 0, width: 0
      })),
      transition('open => closed', [
        animate('0.2s ease-in')
      ]),
      transition('closed => open', [
        animate('0.2s ease-out')
      ]),
    ]),
  ],
})
export class SearchFormParameterGroupComponent implements OnInit {

  @Input() formName: string;
  @Input() parameters!: any;
  @Output() parameterGroupChange: EventEmitter<any>;
  @Output() unBookmarked: EventEmitter<any>;
  @ViewChild('groupInfo') groupInfoPanel!: OverlayPanel;
  userPreference: UserPreference | null = null;
  isToggleSearch: boolean;
  isToggleBookmark: boolean;
  currentParameterGroup: { groupName: string, parameters: any } | null;
  newParameterGroupName: string;
  form: FormGroup;
  submitted: boolean;
  isPreferenceLoading: boolean = true;

  constructor(
    private userService: UserService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private fb: FormBuilder,
    private confirmationService: ConfirmationService) {
    this.formName = '';
    this.isToggleSearch = false;
    this.isToggleBookmark = false;
    this.submitted = false;
    this.newParameterGroupName = '';
    this.currentParameterGroup = null;
    this.form = this.fb.group({
      parameterGroupName: ['', Validators.required]
    });
    this.parameterGroupChange = new EventEmitter<any>();
    this.unBookmarked = new EventEmitter<any>();
  }

  get parameterGroups(): { groupName: string, parameters: any }[] {
    if(!this.userPreference) {
      return [];
    }
    const formParameterGroup = this.userPreference.searchFormParameterGroups.find(x => x.formName === this.formName);
    return isNullOrEmpty(formParameterGroup) ?
      [] : (formParameterGroup as SearchFormParameterGroup).parameterGroups;
  }

  ngOnInit(): void {
    this.initUserPreference();
  }

  private initUserPreference(): void {
    this.isPreferenceLoading = true;

    this.userPreference = null;
    this.userService.getUserPreferences()
      .pipe(
        finalize(() => this.isPreferenceLoading = false)
      )
      .subscribe({
        next: (data) => {
          if (data) {
            this.userPreference = data;
            // 將 db 資料寫回 local storage
            this.userService.updateUserPreferenceInLocalStorage(data);
          } else {
            // API 成功 (200 OK) 但回傳 Null -> 視為新使用者
            this.userPreference = new UserPreference();
          }
        },
        error: (err) => {
          console.error('Failed to load user preferences', err);
          this.toastService.error(this.translateService.instant('Common.Message.UserPreferenceLoadFail'));
          this.userPreference = new UserPreference();
        }
      })
  }

  toggleSearch(event: any): void {
    this.isToggleSearch = !this.isToggleSearch;
  }

  /** 清除書籤選取狀態，供父元件在 Reset 時呼叫 */
  clearSelection(): void {
    this.currentParameterGroup = null;
    this.isToggleBookmark = false;
  }

  toggleBookmark(event: any): void {
    // 安全檢查：如果還在載入中，禁止操作
    if (this.isPreferenceLoading) {
      return;
    }

    if (this.isToggleBookmark) {
      // Delete parameter group
      this.confirmationService.confirm({
        target: event.target,
        message: '是否刪除此參數組合?',
        acceptLabel: '是',
        rejectLabel: '否',
        accept: () => {
          this.deleteParameterGroup();
          this.isToggleBookmark = false;
          this.unBookmarked.emit({ originalEvent: event, data: this.parameters });
        }
      });
    } else {
      // Add parameter group
      this.groupInfoPanel.toggle(event, event.target);
    }
  }

  saveBtnClick(event: any): void {
    this.saveParameterGroup();
  }

  parameterGroupDDLChange(event: any): void {
    this.parameters = event.value?.parameters;
    this.isToggleBookmark = !isNullOrEmpty(event.value);
    this.parameterGroupChange.emit({ originalEvent: event, data: this.parameters });
  }

  groupInfoSaveBtnClick(event: any): void {
    if (this.form.invalid) {
      this.submitted = true;
      return;
    }

    // 防止載入未完成就存檔
    if(this.isPreferenceLoading) {
      return;
    }

    this.saveParameterGroup();
    this.currentParameterGroup = this.parameterGroups.find(x => x.groupName === this.newParameterGroupName) ?? null;
    this.newParameterGroupName = '';
    this.isToggleBookmark = true;
    this.submitted = false;
    this.form.reset();
    this.groupInfoPanel.hide();
  }

  private saveParameterGroup(): void {
    if (!this.userPreference) {
      return;
    }
    const parameterGroup = { groupName: this.newParameterGroupName, parameters: this.parameters };
    const formParameterGroup = this.userPreference.searchFormParameterGroups.find(x => x.formName === this.formName);
    if (isNullOrEmpty(formParameterGroup)) {
      const formGroup = { formName: this.formName, parameterGroups: [parameterGroup] };
      this.userPreference.searchFormParameterGroups.push(formGroup);
    } else {
      if (isNullOrEmpty(this.currentParameterGroup)) {
        formParameterGroup?.parameterGroups.push(parameterGroup);
      } else {
        const idx = this.parameterGroups.findIndex(x => x.groupName === this.currentParameterGroup?.groupName);
        if (idx > -1) {
          this.parameterGroups[idx].parameters = this.parameters;
        }
      }
    }

    this.userService.preference = this.userPreference;
    this.userPreference = this.userService.preference;
  }

  private deleteParameterGroup(): void {
    if (!this.userPreference) {
      return;
    }
    const idx = this.parameterGroups.findIndex(x => x.groupName === this.currentParameterGroup?.groupName);
    if (idx < 0) {
      return;
    }
    this.parameterGroups.splice(idx, 1);
    this.userService.preference = this.userPreference;
    this.userPreference = this.userService.preference;
    this.currentParameterGroup = null;
  }
}
